/**
 * Voorbereidingsscherm vóór fysieke beamer-meeting:
 * 1) PDF upload
 * 2) Automatische pagina-analyse → tijdlijn met slots
 * 3) Tijdlijn bewerken (labels, duur, oog-toggle, slots invoegen)
 * 4) Start presentatie
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { COLLECTIONS } from '../meetingConstants';
import TemplateUpload from './TemplateUpload';
import PresentationTimelineEditor from './PresentationTimelineEditor';
import TemplateZoneEditor from './TemplateZoneEditor';
import { ProjectorScreenIcon } from '../../icons/phosphorSystem';
import { withAlpha, lighten } from '../../../utils/colorUtils';
import { loadPdfDocumentFromUrl } from '../loadPdfDocumentFromUrl';
import { extractAllPages } from '../pdfPageExtractor';
import type { PdfPageInfo } from '../pdfPageExtractor';
import { classifyPages } from '../classifyPdfPages';
import { savePresentationTimeline, loadPresentationTimeline } from '../presentationTimelineService';
import type { PresentationSlot, TemplateZone } from '../networkClubTypes';

export type PhysicalPresentationSetupProps = {
  entityId: string;
  clubName: string;
  primaryColor: string;
  accentColor: string;
  onClose: () => void;
  onStartPresentation: () => void;
};

type AnalysisState =
  | { status: 'idle' }
  | { status: 'loading'; progress: number; total: number }
  | { status: 'done' }
  | { status: 'error'; message: string };

const PhysicalPresentationSetup: React.FC<PhysicalPresentationSetupProps> = ({
  entityId,
  clubName,
  primaryColor,
  accentColor,
  onClose,
  onStartPresentation,
}) => {
  const [templatePdfUrl, setTemplatePdfUrl] = useState<string | null>(null);
  const [pitchTemplateUrl, setPitchTemplateUrl] = useState<string | null>(null);
  const [pitchZones, setPitchZones] = useState<TemplateZone[]>([]);
  const [slots, setSlots] = useState<PresentationSlot[]>([]);
  const [thumbnails, setThumbnails] = useState<Map<number, string>>(new Map());
  const [analysis, setAnalysis] = useState<AnalysisState>({ status: 'idle' });
  const [saving, setSaving] = useState(false);
  const analysedUrlRef = useRef<string | null>(null);

  // Listen for meetingSettings (templatePdfUrl + pitchTemplate)
  useEffect(() => {
    const unsub = onSnapshot(doc(db, COLLECTIONS.SETTINGS, entityId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as {
        templatePdfUrl?: string;
        pitchTemplateUrl?: string;
        pitchTemplateZones?: TemplateZone[];
      };
      const tplUrl = typeof data.templatePdfUrl === 'string' && data.templatePdfUrl ? data.templatePdfUrl : null;
      setTemplatePdfUrl((prev) => (prev === tplUrl ? prev : tplUrl));
      const ptUrl = typeof data.pitchTemplateUrl === 'string' && data.pitchTemplateUrl ? data.pitchTemplateUrl : null;
      setPitchTemplateUrl((prev) => (prev === ptUrl ? prev : ptUrl));
      if (Array.isArray(data.pitchTemplateZones)) {
        setPitchZones(data.pitchTemplateZones);
      }
    });
    return () => unsub();
  }, [entityId]);

  // Load saved timeline on mount
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const tl = await loadPresentationTimeline(entityId);
        if (!cancelled && tl && tl.slots.length > 0) {
          setSlots(tl.slots);
          setAnalysis({ status: 'done' });
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [entityId]);

  // When a new PDF is uploaded, analyze all pages
  const analyzePdf = useCallback(async (url: string) => {
    if (analysedUrlRef.current === url) return;
    analysedUrlRef.current = url;
    setAnalysis({ status: 'loading', progress: 0, total: 0 });
    try {
      const pdf = await loadPdfDocumentFromUrl(url);
      const total = pdf.numPages;
      setAnalysis({ status: 'loading', progress: 0, total });

      const pages: PdfPageInfo[] = await extractAllPages(pdf, (done, t) => {
        setAnalysis({ status: 'loading', progress: done, total: t });
      });

      // Build thumbnails map
      const thumbMap = new Map<number, string>();
      for (const p of pages) thumbMap.set(p.pageNumber, p.thumbnailDataUrl);
      setThumbnails(thumbMap);

      // Classify pages into initial slots
      const classified = classifyPages(pages);
      setSlots(classified);
      setAnalysis({ status: 'done' });

      // Auto-save initial classification
      await savePresentationTimeline(entityId, classified, total);
    } catch (err) {
      console.error('[PhysicalPresentationSetup] PDF analysis failed:', err);
      setAnalysis({ status: 'error', message: 'PDF-analyse mislukt. Probeer opnieuw.' });
      analysedUrlRef.current = null;
    }
  }, [entityId]);

  // Trigger analysis when URL appears/changes
  useEffect(() => {
    if (templatePdfUrl && analysis.status !== 'loading') {
      void analyzePdf(templatePdfUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templatePdfUrl]);

  // Save on slot changes (debounced)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSlotsChange = useCallback((next: PresentationSlot[]) => {
    setSlots(next);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await savePresentationTimeline(entityId, next, thumbnails.size || next.filter(s => s.type === 'pdf_page').length);
      } catch { /* silent */ }
      setSaving(false);
    }, 800);
  }, [entityId, thumbnails.size]);

  useEffect(() => () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
  }, []);

  // Save pitch template zones (debounced)
  const zoneSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleZonesChange = useCallback((next: TemplateZone[]) => {
    setPitchZones(next);
    if (zoneSaveRef.current) clearTimeout(zoneSaveRef.current);
    zoneSaveRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await setDoc(
          doc(db, COLLECTIONS.SETTINGS, entityId),
          { pitchTemplateZones: next, updatedAt: serverTimestamp() },
          { merge: true },
        );
      } catch { /* silent */ }
      setSaving(false);
    }, 600);
  }, [entityId]);

  useEffect(() => () => {
    if (zoneSaveRef.current) clearTimeout(zoneSaveRef.current);
  }, []);

  // Save pitch template URL after upload
  const handlePitchTemplateUploaded = useCallback(async (url: string) => {
    setPitchTemplateUrl(url);
    try {
      await setDoc(
        doc(db, COLLECTIONS.SETTINGS, entityId),
        { pitchTemplateUrl: url, updatedAt: serverTimestamp() },
        { merge: true },
      );
    } catch { /* silent */ }
  }, [entityId]);

  const headerTint = lighten(primaryColor, 0.94);
  const borderSoft = withAlpha(primaryColor, 0.12);
  const iconBg = withAlpha(primaryColor, 0.1);

  const enabledSlots = slots.filter((s) => s.enabled);
  const totalMinutes = enabledSlots.reduce((sum, s) => sum + s.durationMinutes, 0);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'rgba(15, 23, 42, 0.38)', backdropFilter: 'blur(8px)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="physical-setup-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[520px] rounded-[20px] sm:rounded-[24px] overflow-hidden flex flex-col max-h-[min(92vh,800px)] shadow-lg"
        style={{
          background: '#fff',
          border: `1px solid ${borderSoft}`,
          boxShadow: `0 24px 48px rgba(15, 23, 42, 0.1), 0 0 0 1px ${withAlpha(primaryColor, 0.04)}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-5 sm:px-6 pt-5 pb-4 shrink-0 border-b"
          style={{
            background: `linear-gradient(180deg, ${headerTint} 0%, #ffffff 72%)`,
            borderColor: borderSoft,
          }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div
                className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: iconBg, color: primaryColor }}
              >
                <ProjectorScreenIcon size={22} weight="duotone" className="opacity-90" />
              </div>
              <div className="min-w-0 pt-0.5">
                <h2 id="physical-setup-title" className="text-[17px] sm:text-lg font-bold text-slate-800 tracking-tight">
                  Fysieke presentatie
                </h2>
                <p className="text-[13px] text-slate-500 mt-0.5 font-medium truncate">{clubName}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors text-xl leading-none font-light"
              aria-label="Sluiten"
            >
              ×
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-5 space-y-5 bg-[#FAFBFC]">
          {/* 1. PDF Upload */}
          <section>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] mb-2" style={{ color: primaryColor }}>
              1. Presentatie (PDF)
            </h3>
            <div className="rounded-2xl p-4 bg-white border shadow-sm" style={{ borderColor: borderSoft }}>
              <TemplateUpload
                entityId={entityId}
                currentTemplateUrl={templatePdfUrl}
                isHost
                surface="light"
                accentColor={accentColor}
                onUploaded={(url) => setTemplatePdfUrl(url)}
              />
            </div>
          </section>

          {/* 1b. Pitch achtergrond template + zone editor */}
          <section>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] mb-2" style={{ color: primaryColor }}>
              1b. Pitch achtergrond (per lid)
            </h3>
            <p className="text-[12px] text-slate-500 mb-3 leading-relaxed">
              Upload een 1-pagina pitch-template. Dit wordt de achtergrond voor elke pitch — de app vult automatisch naam, foto en functie van het lid in.
            </p>
            <div className="rounded-2xl p-4 bg-white border shadow-sm" style={{ borderColor: borderSoft }}>
              <TemplateUpload
                entityId={entityId}
                currentTemplateUrl={pitchTemplateUrl}
                isHost
                surface="light"
                accentColor={accentColor}
                onUploaded={handlePitchTemplateUploaded}
              />
            </div>
            {pitchTemplateUrl && (
              <div className="mt-3 rounded-2xl p-4 bg-white border shadow-sm" style={{ borderColor: borderSoft }}>
                <p className="text-[11px] font-semibold text-slate-600 mb-2">
                  Plaats zones op de template — sleep om te positioneren, hoek om te resizen:
                </p>
                <TemplateZoneEditor
                  templatePdfUrl={pitchTemplateUrl}
                  zones={pitchZones}
                  onChange={handleZonesChange}
                  primaryColor={primaryColor}
                />
              </div>
            )}
          </section>

          {/* 2. Timeline editor */}
          {analysis.status === 'loading' && (
            <section>
              <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] mb-2" style={{ color: primaryColor }}>
                2. Pagina's analyseren...
              </h3>
              <div className="rounded-2xl p-4 bg-white border shadow-sm" style={{ borderColor: borderSoft }}>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${analysis.total > 0 ? Math.round((analysis.progress / analysis.total) * 100) : 0}%`,
                          background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`,
                        }}
                      />
                    </div>
                    <p className="text-[12px] text-slate-500 mt-1.5">
                      {analysis.progress} / {analysis.total} pagina's geanalyseerd
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {analysis.status === 'error' && (
            <section>
              <div className="rounded-2xl p-4 bg-red-50 border border-red-200 text-[13px] text-red-700">
                {analysis.message}
              </div>
            </section>
          )}

          {analysis.status === 'done' && slots.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: primaryColor }}>
                  2. Tijdlijn bewerken
                </h3>
                {saving && (
                  <span className="text-[10px] text-slate-400 animate-pulse">Opslaan...</span>
                )}
              </div>
              <p className="text-[12px] text-slate-500 mb-3 leading-relaxed">
                Elke PDF-pagina is een slot. Pas labels en tijden aan, sla pagina's over met het oogje, en voeg pitch-rondes of pauzes toe met de <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[10px] font-bold" style={{ background: accentColor }}>+</span> knop.
              </p>
              <div
                className="rounded-2xl bg-white border shadow-sm overflow-hidden"
                style={{ borderColor: borderSoft }}
              >
                <div className="max-h-[40vh] overflow-y-auto p-3">
                  <PresentationTimelineEditor
                    slots={slots}
                    thumbnails={thumbnails}
                    onChange={handleSlotsChange}
                    primaryColor={primaryColor}
                    accentColor={accentColor}
                  />
                </div>
              </div>
            </section>
          )}

          {/* 3. Start */}
          <section>
            <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] mb-2" style={{ color: primaryColor }}>
              {slots.length > 0 ? '3' : '2'}. Start presentatie
            </h3>
            {slots.length > 0 && (
              <p className="text-[12px] text-slate-500 mb-3">
                {enabledSlots.length} actieve slots · ~{totalMinutes} minuten totaal
              </p>
            )}
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 min-h-[44px] rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={onStartPresentation}
                className="flex-1 min-h-[44px] rounded-xl text-white text-sm font-bold transition-all hover:opacity-[0.96] active:scale-[0.99]"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                  boxShadow: `0 6px 20px ${withAlpha(accentColor, 0.28)}`,
                }}
              >
                Start presentatie
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PhysicalPresentationSetup;
